import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, Send, Bot, UserRound, ImagePlus, Loader2, Sparkles, X, Users, BrainCircuit, Plus, Crown } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { adminApi, imgUrl, errMsg } from "../lib/api";

const t = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

function LibraryPicker({ onPick, trigger }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (open && !items) adminApi.get("/admin/library").then((r) => setItems(r.data)).catch(() => setItems([])); }, [open, items]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[340px] p-3" align="start">
        <p className="text-[13px] font-semibold mb-2">Pick from member library</p>
        {!items ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#ee3a70]" /> : !items.length ? <p className="text-[13px] text-[#7a7276]">Library is empty. Upload in the Member library tab.</p> : (
          <div className="grid grid-cols-4 gap-2 max-h-[260px] overflow-y-auto">
            {items.map((i) => (
              <button key={i.id} onClick={() => { onPick(i.url); setOpen(false); }} className="aspect-[3/4] rounded-md overflow-hidden ring-2 ring-transparent hover:ring-[#ee3a70]" data-testid="lib-pick-item">
                <img src={imgUrl(i.url)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
export { LibraryPicker };

export default function MembersPanel() {
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const [sending, setSending] = useState(false);
  const [genFY, setGenFY] = useState(false);
  const [addingMem, setAddingMem] = useState(false);
  const [memInput, setMemInput] = useState("");
  const [savingMem, setSavingMem] = useState(false);
  const [activating, setActivating] = useState(false);
  const endRef = useRef(null);

  const loadMembers = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/members"); setMembers(data); setSel((s) => (s ? data.find((m) => m.id === s.id) || s : s)); }
    catch (e) { toast.error(errMsg(e, "Could not load members")); setMembers([]); }
  }, []);
  useEffect(() => { loadMembers(); const i = setInterval(loadMembers, 10000); return () => clearInterval(i); }, [loadMembers]);

  const loadChat = useCallback(async (id) => {
    try { const { data } = await adminApi.get(`/admin/members/${id}/chat`); setMsgs(data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => {
    if (!sel) return;
    loadChat(sel.id);
    const i = setInterval(() => loadChat(sel.id), 5000);
    return () => clearInterval(i);
  }, [sel?.id, loadChat]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);

  const toggleAI = async (v) => {
    try { await adminApi.patch(`/admin/members/${sel.id}`, { ai_enabled: v }); setSel({ ...sel, ai_enabled: v }); toast.success(v ? "AI is replying again" : "You've taken over this chat"); loadMembers(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !img) return;
    setSending(true);
    try { const { data } = await adminApi.post(`/admin/members/${sel.id}/chat`, { text, image_url: img }); setMsgs((m) => [...m, data]); setText(""); setImg(null); }
    catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const genForYou = async () => {
    setGenFY(true);
    try { await adminApi.post("/admin/generate", { type: "for_you", user_id: sel.id }); toast.success(`AI "For You" post sent to ${sel.name}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setGenFY(false); }
  };

  const addMemory = async (e) => {
    e.preventDefault();
    if (!memInput.trim()) return;
    setSavingMem(true);
    try {
      const { data } = await adminApi.post(`/admin/members/${sel.id}/memories`, { fact: memInput.trim() });
      setSel((s) => ({ ...s, memories: [...(s.memories || []), data] }));
      setMemInput(""); setAddingMem(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSavingMem(false); }
  };

  const deleteMemory = async (mid) => {
    try {
      await adminApi.delete(`/admin/members/${sel.id}/memories/${mid}`);
      setSel((s) => ({ ...s, memories: (s.memories || []).filter((m) => m.id !== mid) }));
    } catch (err) { toast.error(errMsg(err)); }
  };

  const activateVip = async () => {
    setActivating(true);
    try {
      const { data } = await adminApi.post(`/admin/members/${sel.id}/activate`);
      setSel((s) => ({ ...s, subscription: data.subscription, active: true }));
      toast.success(`${sel.name} is now VIP active`);
      loadMembers();
    } catch (err) { toast.error(errMsg(err)); } finally { setActivating(false); }
  };

  useEffect(() => { setAddingMem(false); setMemInput(""); }, [sel?.id]);
  const list = (members || []).filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-5">
      <div className="admin-card p-3 h-[640px] flex flex-col">
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#a79fa3]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#efe3e7] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#ee3a70]/20" data-testid="members-search" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1" data-testid="members-list">
          {!members ? <Loader2 className="w-5 h-5 animate-spin mx-auto mt-6 text-[#ee3a70]" /> : !list.length ? (
            <div className="text-center text-[13px] text-[#7a7276] mt-10"><Users className="w-7 h-7 mx-auto mb-2" />No members yet</div>
          ) : list.map((m) => (
            <button key={m.id} onClick={() => setSel(m)} data-testid={`member-row-${m.id}`}
              className={`w-full text-left p-3 rounded-xl transition-colors ${sel?.id === m.id ? "bg-[#fff0f5]" : "hover:bg-[#fbf5f7]"}`}>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-full bg-[#fde3ea] text-[#ee3a70] grid place-items-center font-semibold text-[14px] shrink-0">{m.name?.[0]?.toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium truncate">{m.name} {!m.ai_enabled && <span className="text-[10px] font-semibold text-white bg-[#1b1719] rounded px-1.5 py-0.5 ml-1">YOU</span>}</p>
                  <p className="text-[12px] text-[#7a7276] truncate">{m.last_message?.text || m.email}</p>
                </div>
                {m.unread > 0 && <span className="text-[11px] font-semibold bg-[#ee3a70] text-white rounded-full min-w-[20px] h-5 px-1.5 grid place-items-center" data-testid="member-unread">{m.unread}</span>}
              </div>
              <p className="text-[11px] mt-1 pl-11"><span className={m.active ? "text-[#227a47]" : "text-[#b42318]"}>{m.active ? "VIP active" : "Inactive"}</span> · <span className="text-[#a79fa3]">{t(m.last_message?.created_at)}</span></p>
            </button>
          ))}
        </div>
      </div>

      <div className="admin-card h-[640px] flex flex-col overflow-hidden">
        {!sel ? <div className="m-auto text-[14px] text-[#7a7276]">Select a member to read and reply to their chat</div> : (
          <>
            <div className="p-4 border-b border-[#f0e4e8] flex flex-wrap items-center gap-3">
              <div className="min-w-0 w-full">
                <p className="font-semibold">{sel.name} <span className="text-[12px] font-normal text-[#7a7276]">{sel.nickname && `("${sel.nickname}")`} {sel.email}</span></p>
                <p className="text-[12px] text-[#7a7276] truncate max-w-[440px] flex items-center gap-2">
                  About: {sel.about || "–"} · Renews {sel.subscription?.renews_at ? new Date(sel.subscription.renews_at).toLocaleDateString() : "-"}
                  {sel.subscription?.status !== "active" && (
                    <button type="button" onClick={activateVip} disabled={activating} className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[#ee3a70] rounded-full px-2.5 py-1 hover:bg-[#d6305f] disabled:opacity-60" data-testid="member-activate-vip-btn">
                      {activating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crown className="w-3 h-3" />} Activate VIP
                    </button>
                  )}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="member-memories">
                  <span className="text-[11px] text-[#7a7276] inline-flex items-center gap-1 mr-0.5"><BrainCircuit className="w-3.5 h-3.5" /> Maya remembers:</span>
                  {!(sel.memories || []).length && !addingMem && <span className="text-[11px] text-[#a79fa3]">Nothing yet</span>}
                  {(sel.memories || []).map((m) => (
                    <span key={m.id} className="inline-flex items-center gap-1 text-[11px] bg-[#fff0f5] text-[#ee3a70] rounded-full pl-2.5 pr-1.5 py-0.5" data-testid="member-memory-chip">
                      {m.fact}
                      <button type="button" onClick={() => deleteMemory(m.id)} className="hover:text-[#b42318]" data-testid="member-memory-delete"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {!addingMem ? (
                    <button type="button" onClick={() => setAddingMem(true)} className="inline-flex items-center gap-0.5 text-[11px] text-[#ee3a70] font-medium" data-testid="member-memory-add-btn"><Plus className="w-3 h-3" /> Add</button>
                  ) : (
                    <form onSubmit={addMemory} className="inline-flex items-center gap-1">
                      <input autoFocus value={memInput} onChange={(e) => setMemInput(e.target.value)} placeholder="e.g. Birthday: March 14" className="h-7 px-2 rounded-full border border-[#efe3e7] text-[11px] w-[180px] focus:outline-none focus:ring-2 focus:ring-[#ee3a70]/20" data-testid="member-memory-input" />
                      <button disabled={savingMem} className="h-7 px-2 rounded-full bg-[#ee3a70] text-white text-[11px]" data-testid="member-memory-save">{savingMem ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}</button>
                      <button type="button" onClick={() => { setAddingMem(false); setMemInput(""); }} className="text-[11px] text-[#a79fa3]" data-testid="member-memory-cancel">Cancel</button>
                    </form>
                  )}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-3">
                <button onClick={genForYou} disabled={genFY} className="admin-ghost" data-testid="member-gen-foryou">{genFY ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI "For You"</button>
                <label className="flex items-center gap-2 text-[13px]">
                  <Switch checked={sel.ai_enabled !== false} onCheckedChange={toggleAI} className="data-[state=checked]:bg-[#ee3a70]" data-testid="member-ai-toggle" />
                  {sel.ai_enabled !== false ? <span className="flex items-center gap-1"><Bot className="w-4 h-4" /> AI replying</span> : <span className="flex items-center gap-1"><UserRound className="w-4 h-4" /> You're replying</span>}
                </label>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#fdf9fa]" data-testid="admin-chat-messages">
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "maya" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] rounded-2xl px-3.5 py-2.5 text-[14px] ${m.sender === "maya" ? "bg-[#fde3ea]" : "bg-white border border-[#f0e4e8]"}`}>
                    {m.image_url && <img src={imgUrl(m.image_url)} alt="" className="rounded-lg max-h-[200px] mb-2" />}
                    {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                    <p className="text-[10.5px] text-[#9a8b8e] mt-1">{m.sender === "maya" ? `Maya · ${m.author === "admin" ? "you" : m.author}` : sel.name} · {t(m.created_at)}</p>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-[#f0e4e8] flex items-center gap-2">
              <LibraryPicker onPick={setImg} trigger={<button type="button" className="icon-btn !w-10 !h-10" data-testid="admin-chat-attach"><ImagePlus className="w-4 h-4" /></button>} />
              {img && <span className="relative"><img src={imgUrl(img)} alt="" className="w-10 h-10 rounded-md object-cover" /><button type="button" onClick={() => setImg(null)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full shadow p-0.5"><X className="w-3 h-3" /></button></span>}
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Reply to ${sel.name} as Maya...`} className="flex-1 h-10 px-4 rounded-full border border-[#efe3e7] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#ee3a70]/20" data-testid="admin-chat-input" />
              <button disabled={sending} className="admin-btn h-10" data-testid="admin-chat-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
            </form>
            {sel.ai_enabled !== false && <p className="text-[11.5px] text-[#7a7276] px-4 pb-2">AI is replying automatically. Messages you send are added too; switch the toggle off to fully take over.</p>}
          </>
        )}
      </div>
    </div>
  );
}
